/**
 * Меню под аватаркой.
 *
 * Всё, что касается самого человека — профиль, справка, обратная связь, тема
 * и язык, — собрано в одном месте. Раньше это были пять отдельных иконок в
 * шапке: строка получалась пёстрой, а угадать, какая из них за что отвечает,
 * можно было только наведением. Снаружи осталась комната — она про игру
 * с другими, а не про настройки, и нужна на виду.
 */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { useUpdateLanguage } from "../api/queries";
import { LANGUAGES, LANGUAGE_NAMES, currentLanguage, setLanguage, type Language } from "../i18n";
import { isStaff, useAuthStore } from "../stores/authStore";
import { setSoundEnabled, soundEnabled } from "../lib/sound";
import { useThemeStore, type Theme } from "../stores/themeStore";
import { Avatar } from "./Avatar";

interface Props {
  onOpenGuide: () => void;
  onOpenFeedback: () => void;
  onOpenAdmin: () => void;
}

export function UserMenu({ onOpenGuide, onOpenFeedback, onOpenAdmin }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const rememberLanguage = useUpdateLanguage();
  const theme = useThemeStore((state) => state.theme);
  const setTheme = useThemeStore((state) => state.set);

  const [open, setOpen] = useState(false);
  // Звук хранится в localStorage, а не в store: его читает модуль, который
  // не знает про React и вызывается из обработчиков карты и ползунка.
  const [sound, setSound] = useState(soundEnabled);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const close = (event: MouseEvent) => {
      if (!box.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  if (!user) return null;

  const language = currentLanguage();

  const chooseLanguage = (next: Language) => {
    setLanguage(next);
    // Язык нужен и серверу: письма должны приходить на понятном человеку
    // языке, а не на языке браузера, из которого он когда-то регистрировался.
    rememberLanguage.mutate(next);
  };

  // Действие закрывает меню само: держать его открытым поверх нового экрана
  // или окна незачем.
  const run = (action: () => void) => () => {
    setOpen(false);
    action();
  };

  return (
    <div className="relative" ref={box}>
      <button
        type="button"
        className="rounded-full transition-opacity hover:opacity-80"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={user.username}
        title={user.username}
      >
        <Avatar url={user.avatarUrl} username={user.username ?? ""} role={user.role} className="h-9 w-9" />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-40 mt-1.5 w-60 overflow-hidden rounded-xl
                     border border-ink-700 bg-ink-900 shadow-panel"
        >
          <div className="flex items-center gap-2.5 border-b border-ink-800 px-3 py-2.5">
            <Avatar url={user.avatarUrl} username={user.username ?? ""} role={user.role} className="h-8 w-8 text-xs" />
            <div className="min-w-0">
              <div className="truncate text-sm text-ink-100">{user.username}</div>
              {isStaff(user) && (
                <div className="text-[11px] text-amber-300">
                  {user.role === "ROLE_ADMIN" ? t("role.admin") : t("role.moderator")}
                </div>
              )}
            </div>
          </div>

          <div className="py-1">
            <MenuLink to="/profile" onNavigate={() => setOpen(false)}>
              {t("nav.profile")}
            </MenuLink>
            {/* Избранное — тот же профиль, только сразу нужной вкладкой:
                отложенное ищут не «где-то в профиле», а по названию. */}
            <MenuLink to="/favorites" onNavigate={() => setOpen(false)}>
              {t("favorites.title")}
            </MenuLink>
            <MenuButton onClick={run(onOpenGuide)}>{t("nav.guide")}</MenuButton>
            <MenuButton onClick={run(onOpenFeedback)}>{t("nav.feedback")}</MenuButton>
            {isStaff(user) && <MenuButton onClick={run(onOpenAdmin)}>{t("admin.goTitle")}</MenuButton>}
          </div>

          {/* Тема и язык — не переходы, а переключатели: их место в меню
              отдельным блоком, где видно текущее значение. */}
          <div className="border-t border-ink-800 px-3 py-2.5">
            <span className="label mb-1.5">{t("theme.label")}</span>
            <Choice
              options={[
                { value: "dark" as Theme, label: t("theme.dark") },
                { value: "light" as Theme, label: t("theme.light") },
              ]}
              active={theme}
              onChoose={setTheme}
            />
          </div>

          <div className="border-t border-ink-800 px-3 py-2.5">
            <span className="label mb-1.5">{t("sound.label")}</span>
            <Choice
              options={[
                { value: "on", label: t("sound.on") },
                { value: "off", label: t("sound.off") },
              ]}
              active={sound ? "on" : "off"}
              onChoose={(value) => {
                const next = value === "on";
                setSoundEnabled(next);
                setSound(next);
              }}
            />
          </div>

          <div className="border-t border-ink-800 px-3 py-2.5">
            <span className="label mb-1.5">{t("language.label")}</span>
            <Choice
              options={LANGUAGES.map((value) => ({ value, label: LANGUAGE_NAMES[value] }))}
              active={language}
              onChoose={chooseLanguage}
            />
          </div>

          <div className="border-t border-ink-800 py-1">
            <MenuButton
              className="text-rust hover:bg-rust/10"
              onClick={run(() => {
                void logout().then(() => navigate("/"));
              })}
            >
              {t("nav.logout")}
            </MenuButton>
          </div>
        </div>
      )}
    </div>
  );
}

/** Ряд взаимоисключающих значений: тема и язык выбираются именно так. */
function Choice<T extends string>({
  options,
  active,
  onChoose,
}: {
  options: { value: T; label: string }[];
  active: T;
  onChoose: (value: T) => void;
}) {
  return (
    <div className="flex gap-1">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className={`flex-1 rounded-lg border px-2 py-1.5 text-xs transition-colors ${
            option.value === active
              ? "border-amber-400/60 bg-amber-400/10 text-amber-300"
              : "border-ink-700 text-ink-300 hover:bg-ink-850"
          }`}
          onClick={() => onChoose(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function MenuLink({
  to,
  children,
  onNavigate,
}: {
  to: string;
  children: React.ReactNode;
  onNavigate: () => void;
}) {
  return (
    <Link
      to={to}
      role="menuitem"
      onClick={onNavigate}
      className="block px-3 py-2 text-sm text-ink-200 transition-colors hover:bg-ink-850"
    >
      {children}
    </Link>
  );
}

function MenuButton({
  children,
  onClick,
  className = "",
}: {
  children: React.ReactNode;
  onClick: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={`block w-full px-3 py-2 text-left text-sm text-ink-200 transition-colors
                  hover:bg-ink-850 ${className}`}
    >
      {children}
    </button>
  );
}
